import React, { useState, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import versionChecker from '../utils/versionCheck';

const GET_CURRENT_VERSION = gql`
  query GetCurrentVersion {
    getCurrentVersion {
      version
      buildTime
      lastUpdated
    }
  }
`;

const ACKNOWLEDGED_VERSION_KEY = 'app_version';
const BUILD_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

const getAcknowledgedVersion = () => {
  return localStorage.getItem(ACKNOWLEDGED_VERSION_KEY) || BUILD_VERSION;
};

const UpdateNotification = () => {
  const [showNotification, setShowNotification] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const { data: versionData } = useQuery(GET_CURRENT_VERSION, {
    pollInterval: 30000, // Check every 30 seconds
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (!versionData?.getCurrentVersion || isUpdating) return;

    const server = versionData.getCurrentVersion.version;
    const acknowledged = getAcknowledgedVersion();

    setServerVersion(server);
    setCurrentVersion(acknowledged);

    // After a forced reload, keep the acknowledged server version and stay quiet
    const justUpdated = localStorage.getItem('app_updating') === 'true';
    if (justUpdated) {
      localStorage.setItem(ACKNOWLEDGED_VERSION_KEY, server);
      localStorage.removeItem('app_updating');
      localStorage.removeItem('dismissed_update_version');
      setCurrentVersion(server);
      setShowNotification(false);
      return;
    }

    // Sync when already on the latest acknowledged version
    if (server === acknowledged) {
      localStorage.setItem(ACKNOWLEDGED_VERSION_KEY, server);
      setShowNotification(false);
      return;
    }

    // Show only until this server version has been acknowledged via Update
    setShowNotification(true);
  }, [versionData, isUpdating]);

  useEffect(() => {
    if (!showNotification) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showNotification]);

  const handleUpdate = () => {
    if (isUpdating || !serverVersion) return;

    setIsUpdating(true);
    setShowNotification(false);

    // Acknowledge this server version BEFORE reload so the modal cannot return
    // after refresh (build-time REACT_APP_VERSION does not change on cache bust).
    localStorage.setItem(ACKNOWLEDGED_VERSION_KEY, serverVersion);
    localStorage.setItem('app_updating', 'true');
    localStorage.removeItem('dismissed_update_version');

    versionChecker.forceReload(serverVersion);
  };

  if (!showNotification || !serverVersion || !currentVersion) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-modal-title"
      aria-describedby="update-modal-description"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '1rem',
        animation: 'updateModalFadeIn 0.25s ease-out'
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
          color: 'white',
          padding: '2rem',
          borderRadius: '16px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.35)',
          width: '100%',
          maxWidth: '420px',
          textAlign: 'center',
          animation: 'updateModalSlideIn 0.3s ease-out'
        }}
      >
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }} aria-hidden="true">
          🚀
        </div>
        <h2
          id="update-modal-title"
          style={{
            fontWeight: 700,
            fontSize: '1.35rem',
            margin: '0 0 0.75rem',
            lineHeight: 1.3
          }}
        >
          Update Required
        </h2>
        <p
          id="update-modal-description"
          style={{
            fontSize: '0.95rem',
            opacity: 0.95,
            margin: '0 0 0.5rem',
            lineHeight: 1.5
          }}
        >
          Version {serverVersion} is ready. Clicking Update is mandatory to continue using the app.
        </p>
        <p
          style={{
            fontSize: '0.875rem',
            opacity: 0.9,
            margin: '0 0 1.5rem',
            lineHeight: 1.5
          }}
        >
          The update is instant — your browser will refresh and you will immediately have the newest version.
        </p>
        <button
          type="button"
          onClick={handleUpdate}
          disabled={isUpdating}
          aria-label="Update app now"
          style={{
            background: 'white',
            border: 'none',
            color: '#1d4ed8',
            padding: '0.85rem 1.75rem',
            borderRadius: '10px',
            fontSize: '1rem',
            fontWeight: 700,
            cursor: isUpdating ? 'wait' : 'pointer',
            minWidth: '180px',
            opacity: isUpdating ? 0.85 : 1,
            transition: 'transform 0.15s ease, opacity 0.15s ease'
          }}
          onMouseOver={(e) => {
            if (!isUpdating) e.currentTarget.style.transform = 'scale(1.03)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          {isUpdating ? 'Updating...' : 'Update Now'}
        </button>
      </div>

      <style>
        {`
          @keyframes updateModalFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes updateModalSlideIn {
            from { transform: translateY(12px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

export default UpdateNotification;
